import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Calendar, Clock, User, Mail, Phone, Briefcase, FileText, ArrowLeft, Check, CreditCard, Star, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const consultationTypes = [
  {
    id: 'discovery',
    name: 'Discovery Call',
    duration: '30 minutes',
    price: 0,
    description: 'Free introductory call to discuss your project needs and see if we\'re a good fit.',
    features: [
      'Project overview discussion',
      'Initial requirements gathering',
      'Q&A session',
      'Next steps recommendation'
    ],
    color: '#10B981',
    popular: false
  },
  {
    id: 'strategy',
    name: 'Strategy Session',
    duration: '2 hours',
    price: 99,
    description: 'In-depth strategy session with our senior consultants to create a comprehensive plan.',
    features: [
      'Detailed project analysis',
      'Technical architecture review',
      'Custom solution roadmap',
      'Budget & timeline estimation',
      'Actionable recommendations',
      'Follow-up summary document'
    ],
    color: '#8B5CF6',
    popular: true
  }
];

const ConsultationBooking = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Type, 2: Details, 3: Confirm
  const [selectedType, setSelectedType] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    date: null,
    time: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, slotsRes, holidaysRes] = await Promise.all([
        axios.get(`${API}/consultation-services`),
        axios.get(`${API}/time-slots`),
        axios.get(`${API}/holidays`)
      ]);
      
      setServices(servicesRes.data.services);
      setTimeSlots(slotsRes.data.slots);
      setHolidays(holidaysRes.data.holidays.map(h => new Date(h)));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const isHoliday = (date) => {
    return holidays.some(holiday => 
      date.toDateString() === holiday.toDateString()
    );
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      date: date,
      time: ''
    });
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.date || !formData.time) {
      toast.error('Please select both date and time');
      return;
    }

    setLoading(true);

    try {
      const bookingData = {
        ...formData,
        consultation_type: selectedType.id,
        consultation_name: selectedType.name,
        duration: selectedType.duration,
        price: selectedType.price,
        date: formData.date.toISOString().split('T')[0]
      };

      // If paid consultation, redirect to Stripe checkout
      if (selectedType.price > 0) {
        const response = await axios.post(`${API}/payments/consultation/create-checkout`, {
          consultation_data: bookingData,
          origin_url: window.location.origin
        });
        
        if (response.data.url) {
          // Store booking data for after payment
          localStorage.setItem('pending_consultation', JSON.stringify(bookingData));
          window.location.href = response.data.url;
          return;
        }
      }

      // Free consultation - book directly
      await axios.post(`${API}/consultations`, bookingData);
      
      toast.success('Consultation Booked!', {
        description: `${selectedType.name} scheduled for ${formData.date.toLocaleDateString()} at ${formData.time}`,
      });
      
      setStep(3);
      
    } catch (error) {
      console.error('Error booking consultation:', error);
      toast.error(error.response?.data?.detail || 'Failed to book consultation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
      <SEO title="Book a Free Consultation - Web Development & AI Solutions" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-8 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#3B82F6]/10 rounded-full blur-[150px]"></div>
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#8B5CF6]/10 rounded-full blur-[120px]"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-[7.6923%]">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/')}
            className="flex items-center gap-2 text-[#3B82F6] hover:text-[var(--text-primary)] transition-colors duration-300 mb-8"
          >
            <ArrowLeft size={20} />
            <span>{step > 1 ? 'Back' : 'Back to Home'}</span>
          </button>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-full mb-6">
            <Calendar size={16} className="text-[#3B82F6]" />
            <span className="text-[#3B82F6] text-sm font-semibold">Book Consultation</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4 leading-tight">
            Schedule Your{' '}
            <span className="bg-gradient-to-r from-[#3B82F6] via-[#8B5CF6] to-[#F97316] bg-clip-text text-transparent">
              Consultation
            </span>
          </h1>
          
          <p className="text-lg text-[var(--text-secondary)] max-w-2xl">
            Choose the consultation type that best fits your needs and let's start building something great together.
          </p>

          {/* Progress Steps */}
          <div className="flex items-center gap-4 mt-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                  step >= s 
                    ? 'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white' 
                    : 'bg-[var(--bg-card)] text-[var(--text-muted)] border border-[var(--border)]'
                }`}>
                  {step > s ? <Check size={18} /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 mx-2 rounded ${
                    step > s ? 'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6]' : 'bg-[var(--border)]'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step Content */}
      <section className="py-12 px-[7.6923%]">
        <div className="max-w-5xl mx-auto">
          
          {/* Step 1: Choose Consultation Type */}
          {step === 1 && (
            <div className="grid md:grid-cols-2 gap-8">
              {consultationTypes.map((type) => (
                <div
                  key={type.id}
                  onClick={() => handleTypeSelect(type)}
                  className={`relative bg-[var(--bg-card)] border-2 rounded-2xl p-8 cursor-pointer transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${
                    type.popular 
                      ? 'border-[#8B5CF6] shadow-xl shadow-[#8B5CF6]/20' 
                      : 'border-[var(--border)] hover:border-[var(--border-hover)]'
                  }`}
                >
                  {type.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 text-sm font-bold text-white rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#A78BFA]">
                      RECOMMENDED
                    </div>
                  )}

                  <div className="flex items-center justify-between mb-6">
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center"
                      style={{ background: `${type.color}20` }}
                    >
                      {type.price === 0 ? (
                        <Zap size={28} style={{ color: type.color }} />
                      ) : (
                        <Star size={28} style={{ color: type.color }} />
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-[var(--text-primary)]">
                        {type.price === 0 ? 'FREE' : `$${type.price}`}
                      </div>
                      <div className="text-[var(--text-muted)] text-sm">{type.duration}</div>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-3">{type.name}</h3>
                  <p className="text-[var(--text-secondary)] mb-6">{type.description}</p>

                  <ul className="space-y-3 mb-8">
                    {type.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div 
                          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: `${type.color}20` }}
                        >
                          <Check size={12} style={{ color: type.color }} />
                        </div>
                        <span className="text-[var(--text-secondary)] text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    className={`w-full py-4 text-lg font-semibold rounded-xl transition-all flex items-center justify-center gap-3 ${
                      type.popular
                        ? 'text-white hover:scale-105 hover:shadow-xl'
                        : 'bg-[var(--bg-elevated)] text-[var(--text-primary)] hover:bg-[var(--bg-dark)] border border-[var(--border)]'
                    }`}
                    style={type.popular ? { background: `linear-gradient(135deg, ${type.color}, ${type.color}CC)` } : {}}
                  >
                    Select {type.name}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Step 2: Fill Details */}
          {step === 2 && selectedType && (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Form */}
              <div className="lg:col-span-2">
                <form onSubmit={handleSubmit} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-8">
                  <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-6">Your Details</h2>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="flex items-center gap-2 text-[var(--text-secondary)] mb-2 text-sm font-medium">
                        <User size={18} className="text-[#3B82F6]" />
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] transition-colors"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-[var(--text-secondary)] mb-2 text-sm font-medium">
                        <Mail size={18} className="text-[#3B82F6]" />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] transition-colors"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="flex items-center gap-2 text-[var(--text-secondary)] mb-2 text-sm font-medium">
                        <Phone size={18} className="text-[#3B82F6]" />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] transition-colors"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-[var(--text-secondary)] mb-2 text-sm font-medium">
                        <Briefcase size={18} className="text-[#3B82F6]" />
                        Service Interest *
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] transition-colors"
                      >
                        <option value="">Select a service</option>
                        {services.map((service, index) => (
                          <option key={index} value={service}>{service}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="flex items-center gap-2 text-[var(--text-secondary)] mb-2 text-sm font-medium">
                        <Calendar size={18} className="text-[#3B82F6]" />
                        Select Date *
                      </label>
                      <DatePicker
                        selected={formData.date}
                        onChange={handleDateChange}
                        minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)} // Tomorrow
                        filterDate={date => !isHoliday(date) && !isWeekend(date)}
                        dateFormat="MMMM d, yyyy"
                        placeholderText="Choose a date"
                        className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] transition-colors"
                        required
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-[var(--text-secondary)] mb-2 text-sm font-medium">
                        <Clock size={18} className="text-[#3B82F6]" />
                        Select Time *
                      </label>
                      <select
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        required
                        disabled={!formData.date}
                        className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] transition-colors disabled:opacity-50"
                      >
                        <option value="">Select a time</option>
                        {timeSlots.map((slot, index) => (
                          <option key={index} value={slot}>{slot}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-8">
                    <label className="flex items-center gap-2 text-[var(--text-secondary)] mb-2 text-sm font-medium">
                      <FileText size={18} className="text-[#3B82F6]" />
                      Project Details (Optional)
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="4"
                      className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] transition-colors resize-none"
                      placeholder="Tell us about your project, goals, and any specific questions..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 text-lg font-semibold text-white rounded-xl transition-all hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                    style={{ background: `linear-gradient(135deg, ${selectedType.color}, ${selectedType.color}CC)` }}
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Booking...
                      </>
                    ) : (
                      <>
                        <Calendar size={20} />
                        {selectedType.price > 0 ? `Book & Pay $${selectedType.price}` : 'Confirm Booking'}
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-6 sticky top-24">
                  <h3 className="text-xl font-bold text-[var(--text-primary)] mb-6">Booking Summary</h3>

                  <div className="space-y-4 pb-6 border-b border-[var(--border)]">
                    <div className="flex justify-between">
                      <span className="text-[var(--text-muted)]">Consultation</span>
                      <span className="text-[var(--text-primary)] font-medium">{selectedType.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text-muted)]">Duration</span>
                      <span className="text-[var(--text-primary)]">{selectedType.duration}</span>
                    </div>
                    {formData.date && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-muted)]">Date</span>
                        <span className="text-[var(--text-primary)]">{formData.date.toLocaleDateString()}</span>
                      </div>
                    )}
                    {formData.time && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-muted)]">Time</span>
                        <span className="text-[var(--text-primary)]">{formData.time}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between py-6 text-xl font-bold">
                    <span className="text-[var(--text-primary)]">Total</span>
                    <span style={{ color: selectedType.color }}>
                      {selectedType.price === 0 ? 'FREE' : `$${selectedType.price}`}
                    </span>
                  </div>

                  <div className="p-4 bg-[var(--bg-dark)] rounded-xl border border-[var(--border)]">
                    <p className="text-[var(--text-muted)] text-sm">
                      {selectedType.price > 0 
                        ? 'Payment will be processed securely after booking confirmation.'
                        : 'You\'ll receive a calendar invite with meeting details.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-12">
                <div className="w-24 h-24 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-8 border-4 border-[#10B981]">
                  <Check size={48} className="text-[#10B981]" />
                </div>
                
                <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-4">
                  Booking Confirmed!
                </h2>
                
                <p className="text-xl text-[var(--text-secondary)] mb-8">
                  Thank you, {formData.name}! Your {selectedType?.name} is scheduled.
                </p>

                <div className="bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl p-6 mb-8">
                  <div className="grid grid-cols-2 gap-4 text-left">
                    <div>
                      <p className="text-[var(--text-muted)] text-sm">Date</p>
                      <p className="text-[var(--text-primary)] font-semibold">{formData.date?.toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-[var(--text-muted)] text-sm">Time</p>
                      <p className="text-[var(--text-primary)] font-semibold">{formData.time}</p>
                    </div>
                    <div>
                      <p className="text-[var(--text-muted)] text-sm">Duration</p>
                      <p className="text-[var(--text-primary)] font-semibold">{selectedType?.duration}</p>
                    </div>
                    <div>
                      <p className="text-[var(--text-muted)] text-sm">Type</p>
                      <p className="text-[var(--text-primary)] font-semibold">{selectedType?.name}</p>
                    </div>
                  </div>
                </div>

                <p className="text-[var(--text-muted)] mb-8">
                  We've sent a confirmation email to <strong className="text-[var(--text-primary)]">{formData.email}</strong> with meeting details.
                </p>

                <button
                  onClick={() => navigate('/')}
                  className="px-8 py-4 text-lg font-semibold text-white rounded-xl transition-all hover:scale-105 hover:shadow-xl inline-flex items-center gap-3"
                  style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                >
                  Back to Home
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <Chatbot proactiveDelay={30000} />
    </div>
  );
};

export default ConsultationBooking;
